import dayjs from 'dayjs'
import PropTypes from 'prop-types'
import React from 'react'
import { twMerge as mergeClassNames } from 'tailwind-merge'

import DealerClosedTag from '@/pages/RoIntake/components/DealerClosedTag'
import { SLA_LATE } from '@/pages/RoStatusDashboard/buckets'

import { formatDuration, hoursInQueue, isDueSoon, isLate, SLA_HOURS } from './queue'
import RowActionCells from './RowActionCells'

/**
 * One Ready or Closed RO, worked in place (WARP-951, WARP-959, WARP-966): a status, the
 * dealer-closed flag, and attachments, then an explicit Save.
 * Mark on 2026-09-17: "I don't want the screen to pop up." The one exception is
 * Outstanding List, whose form has required fields.
 */
const QueueRow = ({
  dispositions,
  edit,
  now,
  onChange,
  onOpen,
  onSave,
  permissions,
  row,
  saving,
  showDealer,
}) => {
  const hours = hoursInQueue(row, now)
  const late = isLate(row, now)
  const dueSoon = isDueSoon(row, now)
  const due = dayjs(row.receivedAt).add(SLA_HOURS, 'hour')
  let barColor = '#1d9e75'
  if (late) barColor = SLA_LATE.swatch
  else if (dueSoon) barColor = '#ef9f27'

  return (
    <tr
      className={mergeClassNames(
        'border-charcoal-100 border-t align-middle',
        saving && 'opacity-60',
      )}
      style={late ? { backgroundColor: SLA_LATE.fill } : undefined}
    >
      <td className="px-4 py-3">
        <span className="flex items-center gap-1.5">
          {/* The flag's own column is there for whoever can set it; others see the tag. */}
          {!permissions.canChangeStatus && row.dealerClosed && <DealerClosedTag />}
          {/* A Ready RO has no page of its own; it opens Manage Repair Order. */}
          <button
            className="text-charcoal-900 text-left font-mono text-sm font-semibold hover:text-blue-800 hover:underline"
            onClick={onOpen}
            type="button"
          >
            {row.roNumber}
          </button>
        </span>
        {showDealer && (
          <span className="text-charcoal-500 block truncate text-xs">{row.dealer.name}</span>
        )}
      </td>

      <td className="text-charcoal-700 whitespace-nowrap p-3 text-sm">
        {/* The year only when it is not this one. */}
        {dayjs(row.receivedAt).format(
          dayjs(row.receivedAt).isSame(now, 'year') ? 'MM/DD' : 'MM/DD/YY',
        )}{' '}
        <span className="text-charcoal-400">{dayjs(row.receivedAt).format('h:mm A')}</span>
      </td>

      <td className="p-3">
        {/* Progress to the SLA. The words carry the state too, not the color alone. */}
        <div className="bg-charcoal-100 h-1.5 w-24 overflow-hidden rounded-full">
          <div
            className="h-full rounded-full"
            style={{
              width: `${Math.min(100, (hours / SLA_HOURS) * 100)}%`,
              backgroundColor: barColor,
            }}
          />
        </div>
        <span
          className={mergeClassNames(
            'mt-1 block whitespace-nowrap text-xs tabular-nums',
            late ? 'font-bold' : 'text-charcoal-600',
          )}
          style={late ? { color: SLA_LATE.ink } : undefined}
        >
          {formatDuration(hours)}
        </span>
      </td>

      <td className="whitespace-nowrap p-3 text-xs">
        {late ? (
          <span className="font-bold" style={{ color: SLA_LATE.ink }}>
            {formatDuration(hours - SLA_HOURS)} overdue · past SLA
          </span>
        ) : (
          <span className={dueSoon ? 'font-semibold text-amber-800' : 'text-charcoal-600'}>
            {due.format('MM/DD h:mm A')}
          </span>
        )}
      </td>

      <RowActionCells
        edit={edit}
        onChange={onChange}
        onSave={onSave}
        options={dispositions}
        permissions={permissions}
        row={row}
        saving={saving}
      />
    </tr>
  )
}

QueueRow.propTypes = {
  // The statuses this queue can move an RO to: `[{ value, label }]`.
  dispositions: PropTypes.arrayOf(
    PropTypes.shape({ label: PropTypes.string, value: PropTypes.string }),
  ).isRequired,
  edit: PropTypes.object.isRequired,
  now: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  // Opens Manage Repair Order for the row.
  onOpen: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  // What this user may do from the row; see `rowPermissions`.
  permissions: PropTypes.object.isRequired,
  row: PropTypes.object.isRequired,
  saving: PropTypes.bool,
  showDealer: PropTypes.bool,
}

QueueRow.defaultProps = {
  saving: false,
  showDealer: false,
}

export default QueueRow
